import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Clock, MapPin, SignIn, SignOut, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
    formatClockTime, formatWorkDate, elapsedSince, readGpsPosition, COMPANY_TZ_LABEL,
} from "@/lib/time";
import { clockBusy } from "@/lib/pwa";

export default function TimeTracking() {
    const [sites, setSites] = useState([]);
    const [active, setActive] = useState(null);
    const [selectedSite, setSelectedSite] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [tick, setTick] = useState(0);
    const [refreshTick, setRefreshTick] = useState(0);

    const [subs, setSubs] = useState([]);
    const [selectedSub, setSelectedSub] = useState("");
    const [outOpen, setOutOpen] = useState(false);

    const [history, setHistory] = useState([]);
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const reload = useCallback(() => setRefreshTick((t) => t + 1), []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([api.get("/sites?active_only=true"), api.get("/time/active")])
            .then(([sitesRes, activeRes]) => {
                if (cancelled) return;
                setSites(Array.isArray(sitesRes.data) ? sitesRes.data : []);
                setActive(activeRes.data?.active || null);
            })
            .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [refreshTick]);

    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams();
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
        api.get(`/time/my-entries${params.toString() ? `?${params}` : ""}`)
            .then(({ data }) => { if (!cancelled) setHistory(Array.isArray(data) ? data : []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [refreshTick, dateFrom, dateTo]);

    // Live ticking timer for the active shift.
    useEffect(() => {
        if (!active) return;
        const id = setInterval(() => setTick((t) => t + 1), 1000);
        return () => clearInterval(id);
    }, [active]);

    const handleClockIn = async () => {
        if (!selectedSite) {
            toast.error("Select your work site first");
            return;
        }
        if (busy) return;
        if (!navigator.onLine) {
            toast.error("Internet connection required to Clock In. Please reconnect and try again.");
            return;
        }
        setBusy(true);
        clockBusy.set(true);
        try {
            const gps = await readGpsPosition();
            const { data } = await api.post("/time/clock-in", { site_id: selectedSite, gps });
            setActive(data);
            setSelectedSite("");
            toast.success(`Clocked in at ${formatClockTime(data.clock_in_local)}`);
            reload();
        } catch (e) {
            // No response object = the request never reached the server.
            if (!e.response) {
                toast.error("Internet connection required to Clock In. Please reconnect and try again.");
            } else {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                reload();
            }
        } finally {
            setBusy(false);
            clockBusy.set(false);
        }
    };

    const openClockOut = async () => {
        if (busy || !active) return;
        if (!navigator.onLine) {
            toast.error("Internet connection required to Clock Out. Please reconnect and try again.");
            return;
        }
        setSelectedSub("");
        if (active.requires_sublocation) {
            try {
                const { data } = await api.get(`/sites/${active.site_id}/sublocations?active_only=true`);
                setSubs(Array.isArray(data) ? data : []);
            } catch (e) {
                if (!e.response) {
                    toast.error("Internet connection required to Clock Out. Please reconnect and try again.");
                } else {
                    toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                }
                return;
            }
        } else {
            setSubs([]);
        }
        setOutOpen(true);
    };

    const confirmClockOut = async () => {
        if (busy) return;
        if (active?.requires_sublocation && !selectedSub) {
            toast.error("Select where you worked today");
            return;
        }
        if (!navigator.onLine) {
            toast.error("Internet connection required to Clock Out. Please reconnect and try again.");
            return;
        }
        setBusy(true);
        clockBusy.set(true);
        try {
            const gps = await readGpsPosition();
            const { data } = await api.post("/time/clock-out", {
                sublocation_id: active?.requires_sublocation ? selectedSub : null,
                gps,
            });
            setOutOpen(false);
            setActive(null);
            toast.dismiss();
            toast.success(`Clocked out at ${formatClockTime(data.clock_out_local)}`, {
                description: `Total ${data.elapsed_display}`,
            });
            reload();
        } catch (e) {
            if (!e.response) {
                toast.error("Internet connection required to Clock Out. Please reconnect and try again.");
            } else {
                toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
                reload();
            }
        } finally {
            setBusy(false);
            clockBusy.set(false);
        }
    };

    return (
        <div className="space-y-6 max-w-2xl mx-auto" data-testid="time-tracking-page">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                    Attendance
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                    Time Tracking
                </h1>
                <p className="text-slate-600 mt-2 text-sm">
                    Record your actual Clock In and Clock Out. All times shown in {COMPANY_TZ_LABEL}.
                </p>
            </div>

            {loading ? (
                <Card className="border-[#E2E8F0]">
                    <CardContent className="p-10 text-center text-slate-500">Loading…</CardContent>
                </Card>
            ) : active ? (
                <Card className="border-2 border-[#0066D9] overflow-hidden" data-testid="active-shift-card">
                    <div className="bg-[#0066D9] px-5 py-3 flex items-center gap-2 text-white">
                        <span className="h-2.5 w-2.5 rounded-full bg-white animate-pulse" />
                        <span className="text-sm font-semibold uppercase tracking-[0.18em]">
                            Currently Working
                        </span>
                    </div>
                    <CardContent className="p-5 sm:p-7 space-y-5">
                        {active.needs_review && (
                            <div className="rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3.5 flex items-start gap-2.5"
                                data-testid="long-open-shift-warning">
                                <Warning size={22} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-bold text-amber-900">You are still clocked in.</div>
                                    <div className="text-sm text-amber-800 mt-0.5">
                                        Your shift has been open for {Math.floor((active.open_seconds || 0) / 3600)}h.
                                        Please confirm whether you need to Clock Out.
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Site</div>
                                <div className="text-xl font-bold text-[#003366] mt-1" data-testid="active-site-name">
                                    {active.site_name}
                                </div>
                            </div>
                            <div>
                                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Clocked In</div>
                                <div className="text-xl font-bold text-[#003366] mt-1" data-testid="active-clock-in-time">
                                    {formatClockTime(active.clock_in_local)}
                                </div>
                            </div>
                        </div>

                        <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 flex items-center justify-between">
                            <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Elapsed</span>
                            <span
                                className="font-mono text-lg font-semibold text-[#003366]"
                                data-testid="active-elapsed"
                                key={tick}
                            >
                                {elapsedSince(active.clock_in_at)}
                            </span>
                        </div>

                        {active.clock_in_gps ? (                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                <MapPin size={14} /> GPS captured at Clock In
                            </div>
                        ) : (
                            <div className="flex items-center gap-1.5 text-xs text-amber-700">
                                <Warning size={14} /> No GPS captured at Clock In
                            </div>
                        )}

                        <Button
                            onClick={openClockOut}
                            disabled={busy}
                            className="w-full h-16 text-lg font-bold bg-[#E6002C] hover:bg-[#c30025] text-white tracking-wide"
                            data-testid="clock-out-button"
                        >
                            <SignOut size={22} className="mr-2" />
                            {busy ? "PLEASE WAIT…" : "CLOCK OUT"}
                        </Button>
                        <p className="text-xs text-slate-500 text-center">
                            The work site cannot be changed after Clock In. Contact an administrator if it's wrong.
                        </p>
                    </CardContent>
                </Card>
            ) : (
                <Card className="border-[#E2E8F0]" data-testid="clock-in-card">
                    <CardContent className="p-5 sm:p-7 space-y-5">
                        <div className="flex items-center gap-2 text-slate-500">
                            <Clock size={18} />
                            <span className="text-sm font-medium">You are not clocked in</span>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Select Work Site <span className="text-[#E6002C]">*</span>
                            </Label>
                            <Select value={selectedSite} onValueChange={setSelectedSite}>
                                <SelectTrigger className="h-14 text-base" data-testid="site-select">
                                    <SelectValue placeholder="Choose your site…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {sites.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-slate-500">No active sites</div>
                                    ) : (
                                        sites.map((s) => (
                                            <SelectItem key={s.id} value={s.id} data-testid={`site-option-${s.name}`}>
                                                {s.name}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                        </div>

                        <Button
                            onClick={handleClockIn}
                            disabled={busy || !selectedSite}
                            className="w-full h-16 text-lg font-bold bg-[#0066D9] hover:bg-[#0052ad] text-white tracking-wide"
                            data-testid="clock-in-button"
                        >
                            <SignIn size={22} className="mr-2" />
                            {busy ? "PLEASE WAIT…" : "CLOCK IN"}
                        </Button>
                        <p className="text-xs text-slate-500 text-center">
                            Your Clock In time is recorded by the server. If your site requires a Data Hall,
                            you'll pick it when you Clock Out.
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* History */}
            <Card className="border-[#E2E8F0]" data-testid="my-time-history-card">
                <CardContent className="p-5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                        <h2 className="text-base md:text-lg font-semibold text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            My Shift History
                        </h2>
                        <div className="flex items-end gap-2">
                            <div className="space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-slate-500">From</Label>
                                <Input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="h-9 text-sm"
                                    data-testid="history-date-from"
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-[10px] uppercase tracking-wider text-slate-500">To</Label>
                                <Input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="h-9 text-sm"
                                    data-testid="history-date-to"
                                />
                            </div>
                            {(dateFrom || dateTo) && (
                                <Button
                                    variant="ghost"
                                    className="h-9 text-slate-500"
                                    onClick={() => { setDateFrom(""); setDateTo(""); }}
                                    data-testid="history-clear-filter"
                                >
                                    Clear
                                </Button>
                            )}
                        </div>
                    </div>

                    {history.length === 0 ? (
                        <div className="py-8 text-center text-sm text-slate-500" data-testid="history-empty">
                            No shifts recorded for this range.
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {history.map((h) => (
                                <div key={h.id} className="py-3 flex items-center justify-between gap-3" data-testid={`history-row-${h.id}`}>
                                    <div className="min-w-0">
                                        <div className="font-semibold text-[#003366] text-sm">
                                            {formatWorkDate(h.work_date)}
                                        </div>
                                        <div className="text-xs text-slate-500 truncate">
                                            {h.site_name}
                                            {h.sublocation_name ? ` · ${h.sublocation_name}` : ""}
                                        </div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-sm text-slate-700 font-mono">
                                            {formatClockTime(h.clock_in_local)} → {h.clock_out_local ? formatClockTime(h.clock_out_local) : "—"}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                            {h.status === "active" ? (
                                                <Badge className="bg-[#0066D9] text-white text-[10px] uppercase">In progress</Badge>
                                            ) : (
                                                h.elapsed_display
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Clock out confirmation / Data Hall selection */}
            <Dialog open={outOpen} onOpenChange={(o) => !busy && setOutOpen(o)}>
                <DialogContent className="sm:max-w-md" data-testid="clock-out-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                            {active?.requires_sublocation ? "Where did you work today?" : "Confirm Clock Out"}
                        </DialogTitle>
                        <DialogDescription>
                            Site: <span className="font-semibold text-[#003366]">{active?.site_name}</span>
                            {" · "}Clocked in {formatClockTime(active?.clock_in_local)}
                        </DialogDescription>
                    </DialogHeader>

                    {active?.requires_sublocation && (
                        <div className="space-y-2 pt-1">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Select Data Hall <span className="text-[#E6002C]">*</span>
                            </Label>
                            <Select value={selectedSub} onValueChange={setSelectedSub}>
                                <SelectTrigger className="h-14 text-base" data-testid="sublocation-select">
                                    <SelectValue placeholder="Choose Data Hall…" />
                                </SelectTrigger>
                                <SelectContent>
                                    {subs.length === 0 ? (
                                        <div className="px-3 py-2 text-sm text-slate-500">
                                            No active Data Halls — contact an administrator.
                                        </div>
                                    ) : (
                                        subs.map((s) => (
                                            <SelectItem key={s.id} value={s.id} data-testid={`sublocation-option-${s.name}`}>
                                                {s.name}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                Required before Clock Out can be completed.
                            </p>
                        </div>
                    )}

                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={() => setOutOpen(false)}
                            disabled={busy}
                            data-testid="clock-out-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmClockOut}
                            disabled={busy || (active?.requires_sublocation && !selectedSub)}
                            className="bg-[#E6002C] hover:bg-[#c30025] text-white h-12 font-bold"
                            data-testid="confirm-clock-out-button"
                        >
                            <CheckCircle size={18} className="mr-1.5" />
                            {busy ? "SAVING…" : "CONFIRM CLOCK OUT"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
